import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentApi } from "../../../shared/api/contentApi";
import { sortItems } from "../../../shared/helpers/SortByName";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import { resolvePagedTotals } from "../../../shared/helpers/pagination";
import { BaseTable } from "../../../components/ui/ant/Table";
import VersionStatusFilter from "../components/VersionStatusFilter";
import CreateVersionDialog from "../components/CreateVersionDialog";
import {Select, Button, Input} from "antd";

import formatDate from '../../../shared/helpers/formatDate';
import UuidCell from '../../../components/ui/UuidCell';
import TableCellPreview from '../../../components/ui/TableCellPreview';

export default function VersionsPage() {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    // Серверная пагинация
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectId, setProjectId] = useState("");
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [openCreateVersionDialog, setOpenCreateVersionDialog] = useState(false);

    useEffect(() => {
        const ac = new AbortController();
        setProjectsLoading(true);

        ContentApi.getProjects({
                page: 1,
                pageSize: 10,
            },
            { signal: ac.signal }
        )
            .then((data) => {
                setProjects(Array.isArray(data?.items) ? data.items : []);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e.message || "Ошибка загрузки проектов");
            })
            .finally(() => !ac.signal.aborted && setProjectsLoading(false));

        return () => ac.abort();
    }, []);

    // Сброс страницы при изменении фильтров
    useEffect(() => {
        setPage(1);
    }, [q, pageSize, projectId, statusFilter]);

    useEffect(() => {
        if (!projectId) {
            setVersions([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError("");

        ContentApi.getVersions(
            {
                projectId,
                status: statusFilter === "ALL" ? undefined : statusFilter,
                page,
                pageSize,
            },
            { signal: ac.signal }
        )
            .then((data) => {
                setVersions(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e.message || "Ошибка загрузки");
            })
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [projectId, statusFilter, page, pageSize]);

    const sortedProjects = useMemo(() => {
        return [...projects].sort((left, right) => {
            const leftTime = Date.parse(left?.updatedAt || 0) || 0;
            const rightTime = Date.parse(right?.updatedAt || 0) || 0;
            return rightTime - leftTime;
        });
    }, [projects]);

    const filteredVersions = useMemo(() => {
        if (!q.trim()) return versions;
        const searchValue = q.trim().toLowerCase();
        return versions.filter((version) => {
            const name = String(version?.name || "").toLowerCase();
            const systemName = String(version?.systemName || "").toLowerCase();
            return name.includes(searchValue) || systemName.includes(searchValue);
        });
    }, [versions, q]);

    // Клиентская сортировка текущей страницы данных
    const pageItems = useMemo(
        () => sortItems(filteredVersions, sortBy, sortDir),
        [filteredVersions, sortBy, sortDir]
    );
    const safePage = Math.min(page, totalPages);

    // Описание колонок для BaseTable
    const columns = [
        { key: "id", header: "ID Версии", render: (value) => <UuidCell value={value} /> },
        { key: "name", header: "Имя", sortable: true },
        { key: "description", header: "Описание", render: (value) => <TableCellPreview value={value} maxLength={32} /> },
        { key: "number", header: "Номер", sortable: true },
        { key: "systemName", header: "Системное имя", sortable: true },
        { key: "status", header: "Статус", groupable: true, render: (value) => <TableCellPreview value={value} maxLength={32} mono /> },
        { key: "createdAt", header: "Создано", render: (value) => <span title={value || '-'}>{formatDate(value)}</span> },
    ];

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">Версии</h1>
                    <p className="text-sm text-zinc-400">Данные с SbContentAPI</p>
                </div>

                <div className="flex flex-wrap gap-2 items-end">
                    <CreateVersionDialog
                        open={openCreateVersionDialog}
                        projectId={projectId}
                        onClose={() => setOpenCreateVersionDialog(false)}
                        onCreated={(created) => {
                            setVersions((prev) => [created, ...prev]);
                            navigate(`/projects/${projectId}/versions/${created.id}`);
                        }}
                    />
                    <div className="flex flex-col min-w-64">
                        <label className="eyebrow mb-1 text-xs font-medium text-zinc-400">Проект</label>
                        <Select
                            value={projectId}
                            onChange={setProjectId}
                            placeholder={projectsLoading ? "Загрузка проектов..." : "Выберите проект"}
                            disabled={projectsLoading || sortedProjects.length === 0}
                            options={sortedProjects.map((project) => ({
                                value: String(project.id),
                                label: project.name || project.systemName || `#${project.id}`,
                            }))}
                        >
                        </Select>
                    </div>

                    <VersionStatusFilter
                        value={statusFilter}
                        onChange={setStatusFilter}
                        disabled={!projectId}
                    />

                    <div className="flex flex-col gap-1">
                        <label className="eyebrow">Поиск</label>
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Имя или systemName…"
                            className=" w-56"
                            disabled={!projectId}
                            name="version-search"
                        />
                    </div>
                </div>
            </header>

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            )}

            {!projectId ? (
                <div className="p-6 text-sm text-zinc-500 text-center border border-dashed border-zinc-700 rounded-xl">
                    Выберите проект, чтобы загрузить версии
                </div>
            ) : loading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : pageItems.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500 text-center border border-dashed border-zinc-700 rounded-xl">
                    Версии не найдены
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Рендеринг нашей компактной таблицы */}
                    <BaseTable
                        columns={columns}
                        data={pageItems}
                        onRowClick={(row) => navigate(`/projects/${projectId}/versions/${row.id}`)}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSortChange={(key, dir) => {
                            setSortBy(key);
                            if (dir) setSortDir(dir);
                        }}
                        pagination={{
                            current: safePage,
                            pageSize,
                            total,
                        }}
                        onChange={(pagination) => {
                            if (pagination?.current) {
                                setPage(pagination.current);
                            }
                            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
                                setPageSize(pagination.pageSize);
                            }
                        }}
                    />
                </div>
            )}
        </section>
    );
}
